import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCcw } from 'lucide-react';
import { logger } from '../../services/logger';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * ErrorBoundary Component
 * Catches runtime errors in the component tree and provides a fallback UI.
 */
// Fix: Use the imported 'Component' directly with explicit generic types <Props, State> to ensure inheritance of 'props' and 'state'.
export class ErrorBoundary extends Component<Props, State> {
  // Fix: Initializing state as a class property to ensure it is correctly typed and recognized by the compiler.
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(_: Error): State {
    return { hasError: true };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('Uncaught exception in component tree', { error, errorInfo });
  }

  public render(): ReactNode {
    // Fix: Accessing 'this.state' which is now properly inherited and typed from Component<Props, State>.
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
          <div className="max-w-md w-full bg-white rounded-[3rem] p-12 shadow-2xl border border-red-100 text-center">
            <div className="w-20 h-20 bg-red-50 rounded-3xl flex items-center justify-center mx-auto mb-8">
              <AlertTriangle className="text-red-500" size={40} />
            </div>
            <h1 className="text-3xl font-black text-slate-900 mb-4 tracking-tighter">
              System Interruption
            </h1>
            <p className="text-slate-500 mb-10 leading-relaxed">
              An unexpected runtime error occurred. Our observability engine has logged the trace.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="w-full py-4 bg-brand-500 text-white rounded-2xl font-black flex items-center justify-center gap-3 hover:bg-brand-600 transition-all shadow-lg shadow-brand-500/20"
            >
              <RefreshCcw size={18} /> Re-initialize Engine
            </button>
          </div>
        </div>
      );
    }

    // Fix: Accessing 'this.props' which is now properly inherited and typed from Component<Props, State>.
    return this.props.children;
  }
}
